import _ from "lodash";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { useApoAuth } from "../../hooks";
import { useLocation } from "react-router-dom";
import { cStyler } from "../../helpers";
import { InlineIcon } from "@iconify/react";
import { useSelector } from "react-redux";
import ReactGA4 from "react-ga4";
import { Button } from "../common/Button";
import jssVariables from "../../jss_setup";

const my_profile = import.meta.env.VITE_APP_EXT_PROFILE_URL;
export var redirectUrl = import.meta.env.VITE_APP_AUTH_SUCCESS_REDIRECT;

const LinksStyles = createUseStyles({
  thcItems: (props) => ({
    ..._.get(props, "customStyles.thcItems"),
  }),
  item: (props) => ({
    fontSize: "small",
    cursor: "pointer",
    justifySelf: "end",
    ..._.get(props, "customStyles.item"),
  }),
  btnContainer: (props) => ({
    ..._.get(props, "customStyles.btnContainer"),
  }),
  upgradeBtn: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.upgradeBtn"),
  }),
  profileIcon: (props) => ({
    fontSize: "1.8rem",
    ..._.get(props, "customStyles.profileIcon"),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    thcItems: (props) => ({
      display: "grid",
      gridTemplateColumns: "max-content max-content",
      gridGap: jssVariables.normal_gap,
      justifyContent: !props.elligibleToBook ? "space-between" : "end",
      ..._.get(props, "customStyles.thcItems.smallscreen"),
    }),
    item: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.item.smallscreen", {}),
    }),
    upgradeBtn: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.upgradeBtn.smallscreen"),
    }),
  },
});
export const Links = ({ elligibleToBook, redirectUrl, revertColor }) => {
  const { t } = useContext(TranslationContext);
  const location = useLocation();

  const isInHotels = location.pathname.includes("hotels");
  const { isAuthenticated } = useApoAuth();
  const { loginWithRedirect } = useAuth0();
  const classes = LinksStyles({
    isInHotels,
    revertColor: isInHotels ? true : revertColor,
    elligibleToBook,
  });

  return (
    <div className={classes.thcItems}>
      {!elligibleToBook && (
        <span className={classes.upgradeBtn}>
          <Button
            text={t("upgrade")}
            size="small"
            bold={true}
            empty={true}
            onClick={() =>
              (window.location.href =
                "https://www.thehotelco.com/#membership-info")
            }
            customStyles={{
              ButtonDefault: {
                borderColor: jssVariables.colors.pink,
                background: jssVariables.colors.pink,
                "&:hover:enabled": {
                  color: jssVariables.colors.pink,
                  background: "white",
                },
              },
            }}
          />
        </span>
      )}
      <div className={classes.item}>
        <div className={classes.btnContainer}>
          <Button
            text={isAuthenticated ? t("myProfile") : t("signInUp")}
            size="small"
            bold={true}
            revert={isInHotels ? true : revertColor}
            onClick={() => {
              isAuthenticated
                ? (window.location.href = my_profile)
                : loginWithRedirect({ redirect_uri: redirectUrl });
            }}
          />
        </div>
      </div>
    </div>
  );
};
Links.defaultProps = { elligibleToBook: false, revertColor: false };
Links.propTypes = {
  elligibleToBook: PropTypes.bool.isRequired,
  redirectUrl: PropTypes.string.isRequired,
  revertColor: PropTypes.bool,
};

const bannerBarStyles = createUseStyles({
  bannerBar: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    justifyContent: "space-between",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    background: "#F1DED0",
    height: "2.5rem",
    alignItems: "center",
    padding: `0 calc(${jssVariables.normal_gap} * 1.5)`,
    ..._.get(props, "customStyles.bannerBar", {}),
  }),
  bannerHeader: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "medium",
    ..._.get(props, "customStyles.bannerHeader", {}),
  }),
  bannerIcons: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(3, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} * 1)`,
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.bannerIcons", {}),
  }),
  socialMediaIcon: (props) => ({
    fontSize: "large",
    cursor: "pointer",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.socialMediaIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    bannerBar: (props) => ({
      fontSize: "medium",
      padding: `0 calc(${jssVariables.normal_gap} * 4)`,
      ..._.get(props, "customStyles.bannerBar.small_screen", {}),
    }),
    bannerIcons: (props) => ({
      gridGap: `calc(${jssVariables.normal_gap} * 3)`,
      ..._.get(props, "customStyles.bannerIcons.small_screen", {}),
    }),
  },
});
export const BannerBar = () => {
  const { type, brand } = useContext(StyleTypeContext);

  const classes = bannerBarStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "bannerBar",
    }),
  });

  const { contact_informations } = useSelector((state) => {
    return {
      contact_informations: _.get(
        state.WebsiteMetadata,
        "metadata.contact_informations"
      ),
    };
  });

  const contact_info = contact_informations
    ? contact_informations.find((info) => info.name === "contact_info")
    : {};

  return (
    <div className={classes.bannerBar}>
      <div className={classes.bannerHeader}>Spend less, travel more</div>
      <div className={classes.bannerIcons}>
        {contact_info && contact_info.facebook_url && (
          <a
            href={contact_info.facebook_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              ReactGA4.event({
                category: "action",
                action: `facebook_navbar`,
                label: `Facebook NavBarTHC`,
              });
            }}>
            <InlineIcon
              icon="fa:facebook"
              className={classes.socialMediaIcon}
            />
          </a>
        )}
        {contact_info && contact_info.instagram_url && (
          <a
            href={contact_info.instagram_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              ReactGA4.event({
                category: "action",
                action: `instagram_navbar`,
                label: `Instagram NavBarTHC`,
              });
            }}>
            <InlineIcon
              icon="fa:instagram"
              className={classes.socialMediaIcon}
            />
          </a>
        )}
        {contact_info && contact_info.whatsapp_url && (
          <a
            href={contact_info.whatsapp_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              ReactGA4.event({
                category: "action",
                action: `whatsapp_navbar`,
                label: `WhatsApp NavBarTHC`,
              });
            }}>
            <InlineIcon
              icon="fa:whatsapp"
              className={classes.socialMediaIcon}
            />
          </a>
        )}
      </div>
    </div>
  );
};
BannerBar.defaultProps = {};
BannerBar.propTypes = {};
